import { IonButton, IonIcon, IonRow } from "@ionic/react";
import { CollapsableTable, CollapseLevel, DataRow, DataRowsFromPLA } from "components/CollapsableTable";
import { Page }                                                      from "components/Page";
import { DvcPage }   from "navigation/DvcPage";
import { RouterMap } from "navigation/RouterMap";
import { FormatNumber, JalFormat, ModCosFormatGCR } from "functions/Formatting";
import { useDeviceConnection }                   from "functions/Context";
import { shortNames, units } from "functions/GeneralTranslations";
import { Glob, useLoc }      from "functions/Language";
import { TYPE_GCR, TYPE_GCRT, TYPE_ICR, TYPE_PLA } from "functions/networking/NetTypes";
import { useLoader }          from "functions/networking/useLoader";
import { FC, useMemo, useState } from "react";

import { chevronCollapseOutline, chevronExpandOutline, speedometerOutline } from "ionicons/icons";


const route = RouterMap.online;

type TableInfo = {
	label: string,
	data: DataRow[],
	LL?: boolean,
}

function useTablesPLA() { // {{{
  const rloc = useLoc(shortNames);
  const loc = useLoc(glob);

  const loader    = useLoader<typeof TYPE_PLA, "Online", null>("Online", null);
  const minLoader = useLoader("Online_Min", null);
  const avgLoader = useLoader("Online_Avg", null);
  const maxLoader = useLoader("Online_Max", null);

  const loaders = [loader, minLoader, avgLoader, maxLoader]

  const mamData = useMemo(() => {
		if (minLoader.data === null || avgLoader.data === null || maxLoader.data === null) return undefined;
		else return { min: minLoader.data.Data, avg: avgLoader.data.Data, max: maxLoader.data.Data };
	}, [minLoader.data, avgLoader.data, maxLoader.data]);

	if (loader.data === null) return { loaders: loaders, tables: [] };

	const result = {
		loaders: loaders,
		tables: [
			[
				{ label: loc.U_LN,
					data: DataRowsFromPLA(
						rloc, units,
						[
							{ label: "U_LN",               3: "U_LN"                                    },
							{ label: "Thd_U_LN",           3: "Thd_U_LN"                                },
							{ label: "Unbalance_LN_U0",                            1: "Unbalance_LN_U0" },
							{ label: "Unbalance_LN_U2",                            1: "Unbalance_LN_U2" },
							{ label: "Under_Deviation_LN", 3: "Under_Deviation_LN"                      },
							{ label: "Over_Deviation_LN",  3: "Over_Deviation_LN"                       },
							{ label: "Frequency",                                  1: "Frequency"       },
						],
						loader.data.Data, mamData
					),
				},

				{ label: loc.U_LL,
					LL: true,
					data: DataRowsFromPLA(
						rloc, units,
						[
							{ label: "U_LL",               3: "U_LL"               },
							{ label: "Thd_U_LL",           3: "Thd_U_LL"           },
							{ label: "Over_Deviation_LL",  3: "Over_Deviation_LL"  },
							{ label: "Under_Deviation_LL", 3: "Under_Deviation_LL" },
						],
						loader.data.Data, mamData
					),
				},
			],

			/* 1 */ [
				{ label: loc.I,
					data: DataRowsFromPLA(
						rloc, units,
						[
							{ label: "Current",            3: "Current",    1: "Current_Zero"   },
							{ label: "Thd_Current",        3: "Thd_Current"                     },
							{ label: "Unbalance_I_I0",                      1: "Unbalance_I_I0" },
							{ label: "Unbalance_I_I2",                      1: "Unbalance_I_I2" },
							{ label: "Tdd",                3: "Tdd"                             },
							{ label: "KFactor",            3: "KFactor"                         },
						],
						loader.data.Data, mamData
					),
				},

				/* 1 */ { label: loc.P,
					data: DataRowsFromPLA(
						rloc, units,
						[
							/* _ */ { label: "CosFi_Mod",          3: "CosFi_Mod", 1: "CosFi_3F_Mod" },
							/* _ */ { label: "Pf_Mod",             3: "Pf_Mod",    1: "Pf_Mod"       },
							/* 2 */ { label: "P",                  3: "P",         1: "P_3F"         },
							/* 3 */ { label: "Q",                  3: "Q",         1: "Q_3F"         },
							/* _ */ { label: "S",                  3: "S",         1: "S_3F"         },
						],
						loader.data.Data, mamData
					)
				},

				{ label: loc.misc,
					data: DataRowsFromPLA(
						rloc, units,
						[
							{ label: "Temperature", 1: "Temperature" },
						],
						loader.data.Data, mamData
					)
				},
			],
		] as TableInfo[][],
	};

	return result;
} // }}}

function useTablesGCR() { // {{{
	const loc = useLoc(glob).gcr;
	const loader = useLoader("Meas", null);

	if (loader.data === null) return { loaders: [loader], tables: [] }

	const data = loader.data.Data;

	return {
		loaders: [loader],
		tables: [
			[
				{ label: loc.cos_title,
					data: [
						{ values: data.cos.now_mod, avg: data.cos.avg, formatter: ModCosFormatGCR, label: loc.now      },
						{ values: data.cos.ind_con,                    formatter: JalFormat   , label: loc.cos_ind_con },
						{ values: data.cos.cap_con,                    formatter: JalFormat   , label: loc.cos_cap_con },
						{ values: data.cos.ind_del,                    formatter: JalFormat   , label: loc.cos_ind_del },
						{ values: data.cos.cap_del,                    formatter: JalFormat   , label: loc.cos_cap_del },
					]
				},

				{ label: loc.power,
					data: [
						{ label: loc.pow_S,
							values: data.power.S,
							max:    data.power.S_max, maxt: data.power.S_maxt,
							formatter: FormatNumber, unit: "VA"
						},
						{ label: loc.pow_P,
							values: data.power.P,
							max:    data.power.P_max, maxt: data.power.P_maxt,
							formatter: FormatNumber, unit: "W"
						},
						{ label: loc.pow_Q,
							values: data.power.Q,
							max:    data.power.Q_max, maxt: data.power.Q_maxt,
							min:    data.power.Q_min, mint: data.power.Q_mint,
							formatter: FormatNumber, unit: "VAr"
						},
						{ label: loc.pow_allowowed_Q,
							values: data.power.allowed_Q,
							formatter: FormatNumber, unit: "VAr"
						},
					]
				},
			],

			[
				{
					label: loc.voltage,
					data: [
						{ label: loc.now,
							values: data.U_LL.now,
							max:    data.U_LL.max, maxt: data.U_LL.maxt,
							formatter: FormatNumber, unit: "V"
						},
						{ label: loc.thd,
							values: data.U_LL.thd,
							max:    data.U_LL.thd_max, maxt: data.U_LL.thd_maxt,
							formatter: FormatNumber, unit: "%"
						},
					]
				},

				{
					label: loc.current,
					data: [
						{ label: loc.now,
							values: data.I.now,
							max:    data.I.max, maxt: data.I.maxt,
							formatter: FormatNumber, unit: "A"
						},
						{ label: loc.thd,
							values: data.I.thd,
							max:    data.I.thd_max, maxt: data.I.thd_maxt,
							formatter: FormatNumber, unit: "%"
						},
					]
				},

				{
					label: loc.misc,
					data: [
						{ label: loc.freq,
							values: data.freq.now,
							max:    data.freq.max, maxt: data.freq.maxt,
							min:    data.freq.min, mint: data.freq.mint,
							formatter: FormatNumber, unit: "Hz"
						},
						{ label: loc.temp,
							values: data.temp.now,
							max:    data.temp.max, maxt: data.temp.maxt,
							formatter: FormatNumber, unit: "°C"
						},
					]
				},
			],
		] as TableInfo[][],
	};
} // }}}

function useTablesICR() { // {{{
	const loc = useLoc(glob).gcr;
	const loader = useLoader("Meas", null);

	if (loader.data === null) return { loaders: [loader], tables: [] }

	const data = loader.data.Data;

	return {
		loaders: [loader],
		tables: [
			[
				{ label: loc.cos_title,
					data: [
						{ values: data.cos.now_mod, avg: data.cos.avg, formatter: ModCosFormatGCR, label: loc.now      },
						{ values: data.cos.ind_con,                    formatter: JalFormat   , label: loc.cos_ind_con },
						{ values: data.cos.cap_con,                    formatter: JalFormat   , label: loc.cos_cap_con },
						{ values: data.cos.ind_del,                    formatter: JalFormat   , label: loc.cos_ind_del },
						{ values: data.cos.cap_del,                    formatter: JalFormat   , label: loc.cos_cap_del },
					]
				},

				{ label: loc.power,
					data: [
						{ label: loc.pow_S,
							values: data.power.S,
							max:    data.power.S_max, maxt: data.power.S_maxt,
							formatter: FormatNumber, unit: "VA"
						},
						{ label: loc.pow_P,
							values: data.power.P,
							max:    data.power.P_max, maxt: data.power.P_maxt,
							formatter: FormatNumber, unit: "W"
						},
						{ label: loc.pow_Q,
							values: data.power.Q,
							max:    data.power.Q_max, maxt: data.power.Q_maxt,
							min:    data.power.Q_min, mint: data.power.Q_mint,
							formatter: FormatNumber, unit: "VAr"
						},
						{ label: loc.pow_allowowed_Q,
							values: data.power.allowed_Q,
							formatter: FormatNumber, unit: "VAr"
						},
					]
				},
			],

			[
				{
					label: loc.misc,
					data: [
						{ label: loc.temp,
							values: data.temp.now,
							max:    data.temp.max, maxt: data.temp.maxt,
							formatter: FormatNumber, unit: "°C"
						},
					]
				},
			],
		] as TableInfo[][],
	};
} // }}}

const Content: FC = () => {
	const loc = useLoc(glob);

	const connection = useDeviceConnection();
	let tables;
	switch (connection.device._type) {
		case TYPE_PLA: tables = useTablesPLA(); break;
		case TYPE_GCR: tables = useTablesGCR(); break;
		case TYPE_GCRT: tables = useTablesGCR(); break;
		case TYPE_ICR: tables = useTablesICR(); break;
		default:
			console.warn("Unknown device", connection.device["_type"]);
			tables = { loaders: [], tables: [] };
			break;
	}

	const [collapseLevel, setCollapseLevel] = useState(CollapseLevel.openSome);
	function MixCollapse() { setCollapseLevel(CollapseLevel.mixed); }
	function ToggleCollapse() {
		setCollapseLevel(old => {
			switch (old) {
				case CollapseLevel.openSome: return CollapseLevel.openAll;
				case CollapseLevel.openAll:  return CollapseLevel.closeAll;
				default:                     return CollapseLevel.openSome;
			}
		});
	}
	const expandButton = <IonButton onClick={ToggleCollapse}>
		<IonIcon icon={collapseLevel === CollapseLevel.openAll ? chevronCollapseOutline : chevronExpandOutline} />
	</IonButton>;

	return <Page
		title={loc.title}
		loaders={tables.loaders}
		additionalButtons={expandButton}
	>
		<IonRow style={{marginInline: ".25%"}}>
			{tables.tables.map((group, i) => <span key={i} className="group">
				{group.map((table, i) => <CollapsableTable
					key={i}
					title={table.label}
					data={table.data}
					LL={table.LL}
					collapse={collapseLevel}
					onToggle={MixCollapse}
				/>)}
			</span>)}
		</IonRow>
	</Page>
}

const glob = Glob({
	en: {
		title: "Tables",
		minMax: "Show minima, averages and maxima",

		U_LN: "Phase Voltage",
		U_LL: "Line Voltage",
		I: "Current",
		P: "Power",
		misc: "Other",

		values: ["Values"],

		gcr: {
			now: "Actual",
			min: "Min",
			avg: "Average",
			max: "Max",

			cos_title: "cos φ",
			cos_ind_con: "L, P+",
			cos_cap_con: "C, P+",
			cos_ind_del: "L, P-",
			cos_cap_del: "C, P-",

			current: "Current",
			voltage: "Line voltage",

			thd: "THD",
			thd_max: "Max THD",

			power: "Power",
			pow_S: "Apparent",
			pow_P: "Active",
			pow_Q: "Reactive",
			pow_allowowed_Q: "Allowed Q",

			misc: "Other",
			freq: "Frequency",
			temp: "Temperature",
		},
	},
	cs: {
		title: "Tabulky",
		minMax: "Zobrazit minima, průměry a maxima",

		U_LN: "Fázové napětí",
		U_LL: "Sdružené napětí",
		I: "Proud",
		P: "Výkon",
		misc: "Jiné",

		values: ["Hodnoty"],

		gcr: {
			now: "Aktuální",
			min: "Min",
			avg: "Průměr",
			max: "Max",

			cos_title: "cos φ",
			cos_ind_con: "L, P+",
			cos_cap_con: "C, P+",
			cos_ind_del: "L, P-",
			cos_cap_del: "C, P-",

			current: "Proud",
			voltage: "Sdružené napětí",

			thd: "THD",
			thd_max: "Max THD",

			power: "Výkon",
			pow_S: "Zdánlivý",
			pow_P: "Činný",
			pow_Q: "Jalový",
			pow_allowowed_Q: "Povolený Q",

			misc: "Jiné",
			freq: "Frekvence",
			temp: "Teplota",
		},
	},
	pg: {
		title: "Tabelas",
		minMax: "Mostrar mínimos, médias e máximos",

		U_LN: "Tensão de fase",
		U_LL: "Tensão de linha",
		I: "Corrente",
		P: "Potência",
		misc: "Outros",

		values: ["Valores"],

		gcr: {
			now: "Atual",
			min: "Min",
			avg: "Média",
			max: "Max",

			cos_title: "cos φ",
			cos_ind_con: "L, P+",
			cos_cap_con: "C, P+",
			cos_ind_del: "L, P-",
			cos_cap_del: "C, P-",

			current: "Corrente",
			voltage: "Tensão de linha",

			thd: "THD",
			thd_max: "Max THD",

			power: "Potência",
			pow_S: "Aparente",
			pow_P: "Ativa",
			pow_Q: "Reativa",
			pow_allowowed_Q: "Permitida Q",

			misc: "Outros",
			freq: "Frequência",
			temp: "Temperatura",
		},
	},
	es: {
		title: "Tablas",
		minMax: "Mostrar mínimos, medias y máximos",

		U_LN: "Tensión de fase",
		U_LL: "Tensión de línea",
		I: "Corriente",
		P: "Potencia",
		misc: "Otros",

		values: ["Valores"],

		gcr: {
			now: "Actual",
			min: "Min",
			avg: "Media",
			max: "Max",

			cos_title: "cos φ",
			cos_ind_con: "L, P+",
			cos_cap_con: "C, P+",
			cos_ind_del: "L, P-",
			cos_cap_del: "C, P-",

			current: "Corriente",
			voltage: "Tensión de línea",

			thd: "THD",
			thd_max: "Max THD",

			power: "Potencia",
			pow_S: "Aparente",
			pow_P: "Activa",
			pow_Q: "Reactiva",
			pow_allowowed_Q: "Permitida Q",

			misc: "Otros",
			freq: "Frecuencia",
			temp: "Temperatura",
		},
	},
} as const);

export const POnline : DvcPage = {
  page: Content,
  path: route,
  title: glob,
  icon: speedometerOutline,
}

